import React, { useState, useEffect, useCallback } from 'react';
import { Activity } from 'lucide-react';
import { API_URL } from '../config';
import api, { getApiBaseUrl } from '../lib/api';
import { DEV_BACKEND_CANDIDATES, rememberApiBase } from '../lib/apiBase';

const initialStatus = {
  platform: 'checking',
  database: 'checking',
  network: 'checking',
  anthropic: 'checking',
  openai: 'checking',
  agent: 'checking'
};

const SystemStatusIndicator = ({ darkMode, inline = false }) => {
  const [status, setStatus] = useState(initialStatus);
  const [statusDetails, setStatusDetails] = useState({}); // { anthropic: { error }, openai: { error }, ... }
  const [showDetails, setShowDetails] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [initialReconnectAttempted, setInitialReconnectAttempted] = useState(false);
  const [showPortPopup, setShowPortPopup] = useState(false);
  const [portStatuses, setPortStatuses] = useState({});

  const appendLog = useCallback((message, info = null) => {
    setLogs((prev) => {
      const entry = {
        timestamp: new Date().toISOString(),
        message,
        detail:
          info && typeof info === 'object'
            ? JSON.stringify(info, Object.getOwnPropertyNames(info), 2)
            : info || null
      };
      const next = [entry, ...prev];
      return next.slice(0, 50);
    });
  }, []);

  const lastAiFailureRef = React.useRef(0);
  const AI_FAILURE_LOG_THROTTLE_MS = 60000; // Log at most once per minute

  const checkSystemStatus = useCallback(async () => {
    const newStatus = { ...initialStatus };
    const baseUrl = getApiBaseUrl() || API_URL;

    try {
      const response = await api.get('/api/leads/ai-status', {
        baseURL: baseUrl,
        timeout: 10000
      });
      const data = response?.data || {};
      newStatus.platform = data?.platform?.status || 'unknown';
      newStatus.anthropic = data?.anthropic?.status || 'unknown';
      newStatus.openai = data?.openai?.status || 'unknown';
      newStatus.database = data?.database?.status || 'unknown';
      newStatus.network = data?.network?.status || 'unknown';
      setStatusDetails({
        anthropic: data?.anthropic || {},
        openai: data?.openai || {},
        database: data?.database || {},
        platform: data?.platform || {}
      });
    } catch (error) {
      const now = Date.now();
      if (now - lastAiFailureRef.current >= AI_FAILURE_LOG_THROTTLE_MS) {
        lastAiFailureRef.current = now;
        console.warn('[SystemStatusIndicator] AI status check failed:', error?.message);
      }
      appendLog('AI status check failed', error?.message || error);
      newStatus.platform = 'offline';
      newStatus.database = 'unknown';
      newStatus.network = 'unknown';
      newStatus.anthropic = 'unknown';
      newStatus.openai = 'unknown';
    }

    try {
      const response = await api.get('/api/support/chat/health', {
        baseURL: baseUrl,
        timeout: 5000
      });
      newStatus.agent = response?.status === 200 ? 'online' : 'offline';
    } catch (error) {
      newStatus.agent = 'unknown';
      appendLog('Support chat health check failed', error?.message || error);
    }

    setStatus(newStatus);
  }, [appendLog]);

  useEffect(() => {
    checkSystemStatus();
    const interval = setInterval(checkSystemStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [checkSystemStatus]);

  const scanAndConnect = useCallback(async () => {
    if (isReconnecting) return;
    setIsReconnecting(true);
    appendLog('Attempting reconnection...');

    const baseUrl = getApiBaseUrl() || API_URL;
    // First try current API base
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 3000);
      const res = await fetch(`${baseUrl}/api/test-connection`, {
        signal: ctrl.signal,
        credentials: 'omit'
      });
      clearTimeout(timer);
      if (res.ok) {
        appendLog('Connection restored on current port. Refreshing status.');
        await checkSystemStatus();
        setIsReconnecting(false);
        return;
      }
    } catch (error) {
      appendLog('Current port failed', error?.message || error);
    }

    // Try all backend port candidates systematically
    if (typeof window !== 'undefined') {
      const host = window.location?.hostname || '';
      if (/localhost|127\.0\.0\.1/i.test(host)) {
        appendLog('Scanning all backend ports...');

        for (const candidate of DEV_BACKEND_CANDIDATES) {
          if (!candidate) continue;

          try {
            appendLog(`Trying ${candidate}...`);
            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), 1500);
            const res = await fetch(`${candidate}/api/test-connection`, {
              signal: ctrl.signal,
              credentials: 'omit'
            });
            clearTimeout(timer);

            if (res.ok) {
              rememberApiBase(candidate);
              appendLog(`✓ Connected to ${candidate}. Refreshing status.`);
              await checkSystemStatus();
              setIsReconnecting(false);
              return;
            }
          } catch (err) {
            appendLog(`✗ ${candidate} not available`);
          }
        }

        appendLog('⚠ No backend ports responded. Please start the backend server.');
      } else {
        appendLog('Not in local development - cannot probe alternate ports.');
      }
    }

    setIsReconnecting(false);
  }, [isReconnecting, appendLog, checkSystemStatus]);

  const handleReconnect = useCallback(() => {
    setShowPortPopup(true);
  }, []);

  const checkPortStatus = useCallback(async () => {
    const host = window.location?.hostname || '';
    if (!/localhost|127\.0\.0\.1/i.test(host)) return;

    setPortStatuses({});
    const statuses = {};
    for (const candidate of DEV_BACKEND_CANDIDATES) {
      if (!candidate) continue;
      statuses[candidate] = 'checking';
      setPortStatuses({ ...statuses });
    }

    for (const candidate of DEV_BACKEND_CANDIDATES) {
      if (!candidate) continue;
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 1500);
        const res = await fetch(`${candidate}/api/test-connection`, {
          signal: ctrl.signal,
          credentials: 'omit'
        });
        clearTimeout(timer);
        statuses[candidate] = res.ok ? 'online' : 'offline';
      } catch (err) {
        statuses[candidate] = 'offline';
      }
      setPortStatuses({ ...statuses });
    }
  }, []);

  useEffect(() => {
    if (showPortPopup) {
      checkPortStatus();
    }
  }, [showPortPopup, checkPortStatus]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      /localhost|127\.0\.0\.1/i.test(window.location.hostname) &&
      status.platform === 'offline' &&
      !isReconnecting &&
      !initialReconnectAttempted
    ) {
      appendLog('Backend appears offline. Automatically scanning for backend ports...');
      scanAndConnect();
      setInitialReconnectAttempted(true);
    }
  }, [status.platform, isReconnecting, initialReconnectAttempted, scanAndConnect, appendLog]);

  const handlePortSelect = useCallback(async (port) => {
    rememberApiBase(port);
    appendLog(`Switching to ${port}. Reloading page to apply changes.`);
    setShowPortPopup(false);
    // Reload the page so the new API base is picked up by all components
    window.location.reload();
  }, [appendLog]);

  const getStatusColor = (serviceStatus) => {
    switch (serviceStatus) {
      case 'online':
        return 'bg-green-500';
      case 'offline':
        return 'bg-red-500';
      case 'checking':
        return 'bg-yellow-500 animate-pulse';
      case 'unconfigured':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  const attemptAlternateDevPort = async () => {
    if (typeof window === 'undefined') return false;
    const host = window.location?.hostname || '';
    if (!/localhost|127\.0\.0\.1/i.test(host)) return false;
    for (const candidate of DEV_BACKEND_CANDIDATES) {
      if (!candidate) continue;
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 1200);
        const res = await fetch(`${candidate}/api/test-connection`, {
          signal: ctrl.signal,
          credentials: 'omit'
        });
        clearTimeout(timer);
        if (res.ok) {
          rememberApiBase(candidate);
          appendLog('Switched API base to', candidate);
          return true;
        }
      } catch (err) {
        appendLog(`Port probe failed for ${candidate}`, err?.message || err);
      }
    }
    appendLog('No alternate local API ports responded.');
    return false;
  };

  const hasOffline = Object.values(status).some((value) => value === 'offline');

  return (
    <div className={inline ? "relative" : "fixed top-20 right-6 z-50"}>
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-all backdrop-blur-xl ${
          darkMode
            ? 'bg-white/5 hover:bg-white/10 border-white/10'
            : 'bg-white/30 hover:bg-white/40 border-white/20'
        } border`}
        title="System Status - Click for details"
      >
        <Activity size={14} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
        <div className="flex items-center gap-x-2 flex-nowrap">
          {[
            { label: 'Platform', key: 'platform' },
            { label: 'Database', key: 'database' },
            { label: 'Network', key: 'network' },
            { label: 'Anthropic', key: 'anthropic' },
            { label: 'OpenAI', key: 'openai' },
            { label: 'Support', key: 'agent' }
          ].map((item) => (
            <div key={item.key} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${getStatusColor(status[item.key])}`} />
              <span className={`text-[10px] font-medium whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </button>

      {hasOffline && (
        <div className="flex gap-2 mt-2 justify-end">
          <button
            onClick={handleReconnect}
            disabled={isReconnecting}
            className={`text-[10px] px-2 py-1 rounded border ${
              isReconnecting
                ? 'opacity-50 cursor-not-allowed'
                : darkMode
                  ? 'text-slate-200 border-slate-600 hover:bg-slate-700'
                  : 'text-slate-600 border-slate-300 hover:bg-slate-100'
            }`}
          >
            {isReconnecting ? 'Reconnecting…' : 'Reconnect'}
          </button>
          <button
            onClick={() => setShowLogs((prev) => !prev)}
            className={`text-[10px] px-2 py-1 rounded border ${
              darkMode ? 'text-slate-200 border-slate-600 hover:bg-slate-700' : 'text-slate-600 border-slate-300 hover:bg-slate-100'
            }`}
          >
            {showLogs ? 'Hide Log' : 'View Log'}
          </button>
        </div>
      )}

      {showDetails && (
        <>
          {/* Overlay to close dropdown when clicking outside */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDetails(false)}
          />

          {/* Dropdown */}
          <div
            className={`absolute ${inline ? 'left-0' : 'right-0'} mt-2 w-56 rounded-lg shadow-lg border z-50 ${
              darkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}
          >
            <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Activity size={16} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  System Status
                </span>
              </div>
            </div>

            <div className="p-2 space-y-1">
              {[
                { label: 'Platform API', key: 'platform', ready: 'Operational' },
                { label: 'Database', key: 'database', ready: 'Connected' },
                { label: 'Network', key: 'network', ready: 'Stable' },
                { label: 'Anthropic AI', key: 'anthropic', ready: 'Ready' },
                { label: 'OpenAI', key: 'openai', ready: 'Ready' },
                { label: 'Support Agent', key: 'agent', ready: 'Available' }
              ].map((item) => {
                const value = status[item.key];
                const detail = statusDetails[item.key];
                const errorHint = (value === 'offline' || value === 'unconfigured') && detail?.error ? `: ${String(detail.error).slice(0, 40)}` : '';
                const text =
                  value === 'online'
                    ? item.ready
                    : value === 'checking'
                    ? 'Checking...'
                    : value === 'unconfigured'
                    ? `Not configured${errorHint}`
                    : value === 'unknown'
                    ? 'Unknown'
                    : `Offline${errorHint}`;
                const textColor =
                  value === 'online'
                    ? 'text-green-600 dark:text-green-400'
                    : value === 'checking'
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : value === 'unconfigured'
                    ? 'text-amber-600 dark:text-amber-400'
                    : value === 'unknown'
                    ? 'text-gray-600 dark:text-gray-400'
                    : 'text-red-600 dark:text-red-400';
                return (
                  <div key={item.key} className="flex items-center justify-between px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(value)}`} />
                      <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {item.label}
                      </span>
                    </div>
                    <span className={`text-xs ${textColor}`}>{text}</span>
                  </div>
                );
              })}
            </div>

            <div className={`p-2 border-t text-xs text-center ${
              darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
            }`}>
              Last check: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </>
      )}

      {showPortPopup && (
        <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setShowPortPopup(false)} />
          <div
            className={`absolute ${inline ? 'left-0' : 'right-0'} mt-2 w-64 rounded-lg shadow-lg border z-50 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Select Backend Port
              </h3>
            </div>
            <div className="p-2 space-y-1">
              {Object.entries(portStatuses).map(([port, portStatus]) => (
                <div
                  key={port}
                  className={`flex items-center justify-between p-2 rounded-md ${
                    (getApiBaseUrl() || API_URL) === port ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor(portStatus)}`} />
                    <span className={`text-xs font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {port}
                    </span>
                  </div>
                  <button
                    onClick={() => handlePortSelect(port)}
                    disabled={portStatus !== 'online'}
                    className={`text-[10px] px-2 py-1 rounded border ${
                      portStatus !== 'online'
                        ? 'opacity-50 cursor-not-allowed'
                        : darkMode
                        ? 'text-slate-200 border-slate-600 hover:bg-slate-700'
                        : 'text-slate-600 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    Connect
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showLogs && (
        <div
          className={`absolute ${inline ? 'left-0' : 'right-0'} mt-2 w-64 rounded-lg shadow-lg border z-50 ${
            darkMode ? 'bg-gray-900 border-gray-700 text-slate-200' : 'bg-white border-gray-200 text-slate-700'
          } max-h-52 overflow-y-auto`}
        >
          <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className="text-xs font-semibold">Connection Log</p>
          </div>
          <div className="p-3 text-[11px] space-y-1">
            {logs.length === 0 ? (
              <p className={darkMode ? 'text-gray-500' : 'text-gray-500'}>No log entries.</p>
            ) : (
              logs.map((entry, idx) => (
                <div key={idx} className="border-b last:border-b-0 pb-2 mb-2">
                  <p className="font-semibold">
                    {new Date(entry.timestamp).toLocaleTimeString()} — {entry.message}
                  </p>
                  {entry.detail && (
                    <pre className={`mt-1 text-[10px] whitespace-pre-wrap ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      {entry.detail}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemStatusIndicator;
